/**
 * Lightweight FR/EN language context.
 *
 * - Default: FR (the source of truth — French strings live in the JSX).
 * - When `lang === 'en'`, `t(frenchString)` looks the string up in the EN
 *   dictionary; if missing it falls back to the French original (so a
 *   missing entry never crashes the page, it just stays in French).
 * - Persisted to localStorage so the choice survives reloads.
 *
 * Scope (V1): public-facing pages only — Home, Pricing, Booking, About.
 * Mini-games, student dashboard and admin panel stay in French.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { EN } from '../i18n/en';

const LanguageContext = createContext({
  lang: 'fr',
  setLang: () => {},
  t: (s) => s,
});

const STORAGE_KEY = 'as_lang_v1';

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved === 'en' ? 'en' : 'fr';
    } catch {
      return 'fr';
    }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* ignore */ }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const setLang = useCallback((v) => setLangState(v === 'en' ? 'en' : 'fr'), []);

  const t = useCallback((frString) => {
    if (lang === 'fr') return frString;
    return EN[frString] ?? frString;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);
